// import { hashToStyles, toPx } from './utils/styleUtils'
import { normalizeToHex } from '@systemeio/util'
import {
  AlignType,
  BorderColorType,
  BorderStyleType,
  BorderWidthType,
  MarginType,
  PaddingType,
} from './types/stylesTypes'
import { fontWeightAndStyleSwitcher } from './utils/fontWeightAndStyleSwitcher'

type ButtonProps = {
  backgroundColor: string
  text: string
  color: string
  url: string
  height?: number
  width?: number
  fontSize: number
  fontFamily?: string
  fontWeight?: string
  borderStyle?: BorderStyleType
  borderWidth?: BorderWidthType
  borderColor?: BorderColorType
  align: AlignType
  borderRadius?: number
  padding: PaddingType
  margin: MarginType
  outlookWidth?: number
  lineHeight?: number
  fontWeightAndStyle?: string
}

function cssObjToString(obj: Record<string, string | number>, unit = '') {
  if (!obj) {
    return ''
  }
  return Object.entries(obj)
    .filter(Boolean)
    .map(([k, v]) => `${k}:${v}${unit}`)
    .join(';')
    .replace(/[A-Z]/g, match => `-${match.toLowerCase()}`)
}

const LINK_PROTOCOLS = ['https', 'http', 'mailto', 'tel']

function hasScheme(link: string) {
  return LINK_PROTOCOLS.some(lp => link.includes(lp))
}

function isAnchor(link: string) {
  return link[0] === '#'
}

function allEqual(arr: any[]) {
  return arr.every(v => v === arr[0])
}

function Button({
  align,
  url,
  fontSize,
  borderRadius,
  text,
  color,
  backgroundColor,
  borderColor,
  borderStyle,
  borderWidth,
  padding,
  margin,
  outlookWidth = 100,
  lineHeight,
  fontFamily,
  fontWeightAndStyle,
}: ButtonProps) {
  const weightAndStyle = fontWeightAndStyleSwitcher(fontWeightAndStyle ? fontWeightAndStyle : '')
  const fillColor = normalizeToHex(backgroundColor)
  const fillColorString = fillColor ? `fillcolor="${fillColor}"` : ''
  const strokeWeightString = borderWidth ? `strokeweight="${borderWidth.borderTopWidth}px"` : ''
  const strokeColorString = borderColor
    ? `strokecolor="${normalizeToHex(borderColor.borderTopColor)}"`
    : 'stroke="f"'
  const height = padding.paddingTop + padding.paddingTop + fontSize

  const borderStyles = `
    ${
      borderStyle
        ? `
      border-top-style: ${borderStyle.borderTopStyle};
      border-right-style: ${borderStyle.borderRightStyle};
      border-bottom-style: ${borderStyle.borderBottomStyle};
      border-left-style: ${borderStyle.borderLeftStyle};
      `
        : ''
    }
    ${
      borderWidth
        ? `
      border-top-width: ${borderWidth.borderTopWidth}px;
      border-right-width: ${borderWidth.borderRightWidth}px;
      border-bottom-width: ${borderWidth.borderBottomWidth}px;
      border-left-width: ${borderWidth.borderLeftWidth}px;
      `
        : ''
    }
    ${borderColor ? `border-top-color: ${borderColor.borderTopColor};` : ''}
    ${borderColor ? `border-right-color: ${borderColor.borderRightColor};` : ''}
    ${borderColor ? `border-bottom-color: ${borderColor.borderBottomColor};` : ''}
    ${borderColor ? `border-left-color: ${borderColor.borderLeftColor};` : ''}
  `

  const styleString = `
    display: inline-block;
    text-decoration: none;
    color: ${normalizeToHex(color)};
    font-size: ${fontSize}px;
    line-height: ${lineHeight};
    font-family: ${fontFamily ? fontFamily : 'initial'};
    font-weight: ${weightAndStyle.fontWeight};
    font-style: ${weightAndStyle.fontStyle};
    background-color: ${normalizeToHex(backgroundColor)};
    border-radius: ${borderRadius}px;
    ${borderStyles}
  ${cssObjToString(padding as Record<string, number>, 'px')};
  -webkit-text-size-adjust:none;
  mso-hide:all;`

  const colorString = normalizeToHex(color)
  const textStyleString = `
    ${colorString ? `color: ${colorString};` : ``}
    font-family:sans-serif;
    font-size: ${fontSize}px;
  `
  const arcSize = borderRadius ? Math.round((borderRadius * 100) / height) : 0

  const urlWithScheme = url === '' || isAnchor(url) || hasScheme(url) ? url : `https://${url}`

  return (
    <table
      role="presentation"
      // border={0}
      cellPadding="0"
      cellSpacing="0"
      align={align}
      width="100%"
      style={{
        width: '100%',
        marginTop: margin.marginTop,
        marginBottom: margin.marginBottom,
      }}
    >
      <tbody>
        <tr>
          <td
            align={align}
            dangerouslySetInnerHTML={{
              __html: `
                <div>
                  <!--[if mso]>
                    <v:roundrect
                      xmlns:v="urn:schemas-microsoft-com:vml"
                      xmlns:w="urn:schemas-microsoft-com:office:word"
                      href="${urlWithScheme}"
                      style="height:${height}px;v-text-anchor:middle;width:${outlookWidth}px;"
                      arcsize="${arcSize}%"
                      ${fillColorString}
                      ${strokeWeightString}
                      ${strokeColorString}
                    >
                    <w:anchorlock/>
                    <center style="${textStyleString}">${text}</center>
                  </v:roundrect>
                  <![endif]-->
                    <a href="${urlWithScheme}" style="${styleString}">${text}</a>
                </div>`,
            }}
          />
        </tr>
      </tbody>
    </table>
  )
}

export default Button
